// Copyright (c) Docugami, Inc. All rights reserved.

import { Checkbox, Dropdown, Icon, IDropdownOption } from "@fluentui/react";
import { SlidingPanel } from "components/cards/SlidingPanel";
import { IconContainer } from "components/myContentExplorer/NavigationItemGroup";
import { useAppSelector } from "hooks/redux";
import { useEffect, useMemo, useRef, useState } from "react";
import { selectDefaultProjectEnabled } from "store/organization/organizationSlice";
import { selectWorkPaths } from "store/work_path/selectors";
import styled from "styled-components";
import { TelemetryEvent, WorkPathCloneProjectSelectedSchema, WorkPathCreationAdvancedPanelClosedSchema, WorkPathCreationAdvancedPanelOpenedSchema } from "telemetry/TelemetryEvents";
import { Telemetry } from "telemetry/TelemetryManager";
import { getDefaultProjectId, setDefaultProjectId } from "utils/localStorageHelpers";
import { reportWorkPathTypes } from "./WorkPathsHelper";

const AdvancedPanelHeader = styled.div`
    display: flex;
    gap: 5px;
    align-items: center;
    font-weight: bold;
    font-size: 16px;
`;

const CloneProjectContainer = styled.div`
    display: flex;
    flex-direction: row;
    gap: 10px;
    align-items: center;
`

const noneOption: IDropdownOption = { key: 'none', id: 'none-option', text: '' };

interface CloningSlidingPanelProps {
    onSourceWorkPathIdChange: (sourceWorkPathId: string | undefined) => void
}

export const CloningSlidingPanel = (props: CloningSlidingPanelProps) => {
    const [isAdvancePanelOpen, setIsAdvancePanelOpen] = useState(false);
    const organizationId = useAppSelector(state => state.organizations.currentOrganization!.id);
    const [currentDefaultProjectId, setCurrentDefaultProjectId] = useState(getDefaultProjectId(organizationId))
    const [selectedProjectId, setSelectedProjectId] = useState<string | null>(currentDefaultProjectId);
    const workPaths = useAppSelector(state => selectWorkPaths(state, undefined, reportWorkPathTypes));
    const defaultProjectEnabled = useAppSelector(selectDefaultProjectEnabled)
    const selectionChangedCallback = useRef(props.onSourceWorkPathIdChange);

    const workPathOptions = useMemo<IDropdownOption[]>(() => {
        const items: IDropdownOption[] = []
        if (!!selectedProjectId) {
            items.push(noneOption);
        }

        if (!!workPaths?.ids.length) {
            items.push(...workPaths.ids.map(workPathId => ({ key: workPathId as string, id: workPathId as string, text: workPaths.entities[workPathId]!.name })));
        }

        return items;
    }, [workPaths, selectedProjectId]);

    useEffect(() => {
        selectionChangedCallback.current = props.onSourceWorkPathIdChange;
    }, [props.onSourceWorkPathIdChange]);

    useEffect(() => {
        selectionChangedCallback.current?.(selectedProjectId ?? undefined);
    }, [selectedProjectId]);

    useEffect(() => {
        if (defaultProjectEnabled) {
            setDefaultProjectId(organizationId, currentDefaultProjectId);
        }
    }, [organizationId, currentDefaultProjectId, defaultProjectEnabled])

    const onSourceWorkPathChange = (event: React.FormEvent<HTMLDivElement>, option?: IDropdownOption, index?: number) => {
        const isNone = !option || option === noneOption;
        setSelectedProjectId(isNone ? null : option.id!);
        setCurrentDefaultProjectId(null);

        Telemetry.trackEvent<WorkPathCloneProjectSelectedSchema>({
            clonedWorkPathId: option?.id ?? '',
            eventName: TelemetryEvent.WorkPathCloneProjectSelected
        });
    };

    const onClickPanelHeader = () => {
        setIsAdvancePanelOpen(currentValue => {
            const newValue = !currentValue;
            if (newValue) {
                Telemetry.trackEvent<WorkPathCreationAdvancedPanelOpenedSchema>({
                    eventName: TelemetryEvent.WorkPathCreationAdvancedPanelOpened
                });
            }
            else {
                Telemetry.trackEvent<WorkPathCreationAdvancedPanelClosedSchema>({
                    eventName: TelemetryEvent.WorkPathCreationAdvancedPanelClosed
                });

            }
            return newValue;
        });
    }

    return !!workPaths?.ids.length ? (
        <>
            <AdvancedPanelHeader onClick={onClickPanelHeader}>
                Advanced
                <IconContainer isExpanded={!!isAdvancePanelOpen}>
                    <Icon iconName={"ChevronRight"} />
                </IconContainer>
            </AdvancedPanelHeader>
            <SlidingPanel isOpen={isAdvancePanelOpen} durationS={.35}>
                <p>Choose an existing project whose items you want to reuse:</p>
                <CloneProjectContainer>
                    <Dropdown
                        onChange={onSourceWorkPathChange}
                        placeholder="Select Project"
                        options={workPathOptions}
                        selectedKey={selectedProjectId ?? null}
                        styles={{
                            root: {
                                flex: 1
                            },
                            title: {
                                fontStyle: !selectedProjectId ? 'italic' : 'inherit'
                            }
                        }}
                    />
                    {defaultProjectEnabled &&
                        <Checkbox
                            label="Make this my default for all future projects"
                            styles={{
                                root: {
                                    maxWidth: "33%",
                                    minWidth: 125
                                },
                                label: {
                                    alignItems: "center"
                                }
                            }}
                            checked={!!currentDefaultProjectId && currentDefaultProjectId === selectedProjectId}
                            disabled={!selectedProjectId}
                            onChange={(ev, checked?: boolean) => {
                                if (defaultProjectEnabled) {
                                    setCurrentDefaultProjectId(!!checked ? selectedProjectId : null);
                                }
                            }} />
                    }
                </CloneProjectContainer>
            </SlidingPanel>
        </>
    ) : null;
}
